import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * What is happening now, how long it has been happening, and how long it
 * usually takes.
 *
 * Pressing a control here dispatched work on the backend and the screen said
 * almost nothing: the button greyed out, its label changed, and that was the
 * whole of it. The controls did not even pass `loading`, so the spinner the
 * button already knows how to draw never appeared. Nothing counted, nothing
 * said what a normal wait looks like, and an operator watching a still screen
 * cannot tell a job that is running from one that never started.
 *
 * The expected range is stated because it is the difference between waiting
 * and worrying. It comes from what has actually been observed on this
 * deployment, never from a guess, and when a wait passes it the component says
 * so rather than continuing to imply everything is fine.
 */
export function Working({
  what,
  usually,
  overAt,
  since,
}: {
  /** What is running, in the operator's words. */
  what: string;
  /** The observed normal range, e.g. "usually 30 seconds to 2 minutes". */
  usually: string;
  /** Seconds after which this wait is longer than anything observed. */
  overAt?: number;
  /** When it started. Defaults to first render. */
  since?: number;
}) {
  const [start] = useState(() => since ?? Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  const elapsed = seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
  const over = overAt !== undefined && seconds > overAt;

  return (
    <p
      role="status"
      className={`mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption ${over ? 'text-warn' : 'text-ink-2'}`}
    >
      <Loader2 className="h-3.5 w-3.5 flex-none animate-spin" aria-hidden="true" />
      <span>{what}</span>
      <span className="mono tabular-nums">{elapsed}</span>
      <span className={over ? 'text-warn' : 'text-ink-4'}>
        {over ? `longer than any wait recorded on this deployment (${usually})` : usually}
      </span>
    </p>
  );
}
